import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortDirection,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { AppDispatch } from "../../store/store";

import TransactionsDialog from "./TransactionsDialog";
import { useQuery } from "@tanstack/react-query";
import {
  fetchVendorReferrers,
  getTransactionsByEntityId,
} from "../../store/modules/repeatsAndReferrers/repeatsAndReferrersActions";

interface IProps {
  isPro: boolean;
  coAgentIds: number[];
}
const VendorReferrersTab: React.FC<IProps> = ({ isPro, coAgentIds }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: vendorReferrers, loading } = useSelector(
    (state: RootState) => state.repeatsAndReferrers.vendorReferrers,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useQuery({
    queryKey: ["fetchVendorReferrers", coAgentIds],
    queryFn: () => {
      dispatch(fetchVendorReferrers(coAgentIds));

      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  const { data: selectedTransactions = [], isLoading: transactionsLoading } =
    useQuery({
      queryKey: [
        "vendorReferrerTransactions",
        selectedReferrer?.id,
        coAgentIds,
      ],
      queryFn: async () => {
        if (!selectedReferrer?.id) return [];

        const action = getTransactionsByEntityId(selectedReferrer.id, 6);
        const transactions = await action();

        return transactions;
      },
      enabled: !!selectedReferrer?.id,
      refetchOnWindowFocus: false,
    });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReferrer(null);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: isMobile ? "Referrer" : "Vendor Referrer Name",
      minWidth: isMobile ? 120 : 200,
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      flex: isMobile ? 2 : 2,
    },
    {
      field: "count",
      headerName: "Referrals",
      minWidth: isMobile ? 100 : 150,
      flex: 1,
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedReferrer({ id: params.row.id, name: params.row.name });
            setDialogOpen(true);
          }}
          sx={{
            cursor: "pointer",
          }}
        />
      ),
    },
    {
      field: "totalVolume",
      headerName: "Total Volume",
      minWidth: 120,
      flex: 1,
      type: "number",
      filterable: false,
      sortable: true,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
    {
      field: "averagePrice",
      headerName: "Average Price",
      minWidth: 120,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },

    {
      field: "totalGci",
      headerName: "Total GCI",
      minWidth: 120,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
    {
      field: "totalNetIncome",
      headerName: "Total Net Income",
      minWidth: 140,
      flex: 1,
      type: "number",
      sortable: true,
      filterable: false,
      sortingOrder: ["asc", "desc"] as GridSortDirection[],
      valueFormatter: (params: number) => {
        if (params === null || params === undefined) return "";
        return `$${params.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        {/* <Typography variant="subtitle1" gutterBottom>
          Vendor Referrers and their referral counts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on the referral count to see the referrer's transactions.
        </Typography> */}

        <Box sx={{ height: "100%", width: "100%" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : vendorReferrers.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ py: 4 }}>
              No vendor referrers found.
            </Typography>
          ) : (
            <DataGrid
              rows={vendorReferrers}
              columns={columns}
              onRowClick={(params) => {
                setSelectedReferrer({
                  id: params.row.id,
                  name: params.row.name,
                });
                setDialogOpen(true);
              }}
              autoHeight
              density={isMobile ? "compact" : "standard"}
              disableColumnSelector
              disableColumnMenu={isMobile}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20 },
                },
                sorting: {
                  sortModel: [{ field: "count", sort: "desc" }],
                },
              }}
            />
          )}
        </Box>
      </Paper>

      {selectedReferrer && (
        <TransactionsDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          transactions={selectedTransactions}
          title={`Transactions referred by ${selectedReferrer.name}`}
          loading={transactionsLoading}
        />
      )}
    </Box>
  );
};

export default VendorReferrersTab;
